import { useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Toaster } from "./components/ui/sonner";
import { LeadRequestModal } from "./components/LeadRequestModal";
import { SearchWidget } from "./components/SearchWidget";
import { TourResults } from "./components/TourResults";
import { OwnerSection } from "./components/OwnerSection";
import { ManagerCards } from "./components/ManagerCards";
import AdminRequests from "./pages/AdminRequests";
import { TOUR } from "./constants/testIds";
import { generateTours } from "./utils/tours";
import { Plane, Phone, MapPin, Sparkles, ArrowRight, Info, Mail } from "lucide-react";

const scrollToId = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const Header = ({ onOpenLead }) => (
  <header className="w-full bg-cream/90 backdrop-blur-md border-b border-[#F0DFC1] sticky top-0 z-40">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
      <a href="/" data-testid={TOUR.logo} className="flex items-center gap-2.5 shrink-0">
        <div className="w-10 h-10 rounded-full bg-teal flex items-center justify-center rotate-[-12deg]">
          <Plane className="w-5 h-5 text-white -rotate-45" />
        </div>
        <div className="leading-tight">
          <div className="font-display text-xl text-[#063B4E]">Барселона</div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-teal">Туры по всему миру</div>
        </div>
      </a>

      <div data-testid={TOUR.navPhones} className="hidden md:flex items-center gap-6 text-sm">
        <a href="tel:+79223831919" className="flex items-center gap-2 text-[#1B2A3B] hover:text-teal transition-colors">
          <Phone className="w-4 h-4" /> +7 922 383-19-19
        </a>
        <a href="tel:+79129811222" className="flex items-center gap-2 text-[#1B2A3B] hover:text-teal transition-colors">
          <Phone className="w-4 h-4" /> +7 912 981-12-22
        </a>
      </div>

      <Button
        onClick={onOpenLead}
        className="hidden sm:inline-flex bg-coral hover:bg-[#d95a3f] text-white rounded-full h-10 px-5 shadow-md shadow-coral/20"
      >
        Оставить заявку
      </Button>
    </div>
  </header>
);

const Hero = () => (
  <section className="relative overflow-hidden bg-cream grain">
    <div className="absolute -top-24 -right-16 w-[520px] h-[520px] rounded-full bg-sand blur-3xl opacity-70 z-0" />
    <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full bg-teal/10 blur-3xl z-0" />

    <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24 grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
      <div className="animate-fade-up">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-[#F0DFC1] text-teal text-xs uppercase tracking-[0.18em] mb-6">
          <Sparkles className="w-3.5 h-3.5" />
          Онлайн-подбор туров 2026
        </div>
        <h1
          data-testid={TOUR.heroTitle}
          className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#063B4E]"
        >
          Ваше идеальное <em className="text-coral not-italic">путешествие</em>
          <br />
          начинается с одного клика
        </h1>
        <p data-testid={TOUR.heroSubtitle} className="mt-6 text-[#3E5060] text-base sm:text-lg max-w-xl leading-relaxed">
          «Барселона»&nbsp;— команда опытных агентов Наталья, Инна и Юлия.
          Подбираем туры от&nbsp;проверенных туроператоров, сравниваем цены
          и отели, а&nbsp;вы просто выбираете, куда полететь.
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-4">
          <Button
            data-testid={TOUR.heroCtaScroll}
            onClick={() => scrollToId("tour-search")}
            className="bg-teal hover:bg-[#0a616a] text-white rounded-full h-12 px-7 text-base shadow-lg shadow-teal/20"
          >
            Подобрать тур
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <a
            href="tel:+79223831919"
            className="text-[#063B4E] font-semibold underline decoration-coral decoration-2 underline-offset-4 hover:text-coral transition-colors"
          >
            +7 922 383-19-19
          </a>
        </div>

        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#4A5A6B]">
          <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-coral" />120+ направлений</div>
          <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-teal" />Актуальные цены онлайн</div>
          <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-coral" />Договоры с туроператорами</div>
        </div>
      </div>

      {/* Neutral preview card (no fake specific tour) */}
      <div className="relative animate-fade-up hidden lg:block">
        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl">
          <img
            src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=900&q=80"
            alt="Море и пляж — популярные направления"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#063B4E]/45 to-transparent" />
        </div>

        <div
          data-testid={TOUR.heroPreviewCard}
          className="absolute -bottom-6 -left-8 bg-white rounded-2xl p-4 shadow-xl w-64 animate-float-slow"
        >
          <div className="text-[10px] uppercase tracking-widest text-teal mb-1">
            Популярное направление
          </div>
          <div className="font-display text-lg text-[#063B4E] leading-tight">
            Турция, Египет, ОАЭ
          </div>
          <div className="text-sm text-[#4A5A6B] mt-1 leading-snug">
            Подберём актуальные варианты под ваш бюджет
          </div>
          <div className="mt-3 pt-3 border-t border-[#F0DFC1] flex items-baseline justify-between">
            <div>
              <div className="text-2xl font-bold text-coral leading-none">от 42 990 ₽</div>
              <div className="text-[10px] text-[#8A99A8] mt-1">
                Цена ориентировочная. Актуальность уточнит менеджер.
              </div>
            </div>
          </div>
        </div>

        <div className="absolute -top-4 -right-6 bg-teal text-white rounded-2xl px-4 py-3 shadow-xl rotate-3">
          <div className="text-[10px] uppercase tracking-widest opacity-80">В работе</div>
          <div className="font-display text-lg">120+ стран</div>
        </div>
      </div>
    </div>
  </section>
);

const InfoBadge = ({ icon: Icon, title, text }) => (
  <div className="flex items-start gap-3 bg-white/70 backdrop-blur rounded-xl p-4 border border-[#F0DFC1]">
    <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-teal" />
    </div>
    <div>
      <div className="font-semibold text-[#063B4E]">{title}</div>
      <div className="text-sm text-[#4A5A6B]">{text}</div>
    </div>
  </div>
);

const SearchSection = ({ onSearch, onPickForMe, loading, tours, onSelectTour }) => {
  return (
    <section
      id="tour-search"
      data-testid={TOUR.searchSection}
      className="relative bg-gradient-to-b from-cream to-white py-16 sm:py-24"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-coral/10 text-coral text-xs uppercase tracking-[0.18em] mb-5">
            <MapPin className="w-3.5 h-3.5" /> Онлайн-поиск
          </div>
          <h2 data-testid={TOUR.searchTitle} className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#063B4E] leading-[1.05]">
            Подбор тура <em className="text-teal not-italic">онлайн</em>
          </h2>
          <p data-testid={TOUR.searchSubtitle} className="mt-5 text-[#3E5060] text-base sm:text-lg leading-relaxed">
            Вы можете самостоятельно выбрать направление, даты, город вылета, отель и бюджет.
            Система покажет актуальные предложения от туроператоров.
          </p>
        </div>

        <div className="mt-8 sm:mt-10">
          <SearchWidget onSearch={onSearch} onPickForMe={onPickForMe} loading={loading} />
        </div>

        <TourResults tours={tours} loading={loading} onSelect={onSelectTour} />

        {!loading && (!tours || tours.length === 0) && (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <InfoBadge icon={MapPin} title="10 направлений" text="Популярные страны" />
            <InfoBadge icon={Sparkles} title="Отели 3–5★" text="Все категории" />
            <InfoBadge icon={Info} title="Прозрачные цены" text="Уточняем у оператора" />
            <InfoBadge icon={Phone} title="Личный менеджер" text="Ответ за 15 минут" />
          </div>
        )}
      </div>
    </section>
  );
};

const Contacts = ({ onOpenLead }) => (
  <section id="contacts" data-testid={TOUR.contactsSection} className="bg-cream py-20 sm:py-24">
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-teal/10 text-teal text-xs uppercase tracking-[0.18em] mb-4">
          Контакты
        </div>
        <h2 className="font-display text-4xl sm:text-5xl text-[#063B4E]">
          Позвоните — и через 15 минут у вас будут варианты
        </h2>
        <p className="mt-4 text-[#3E5060]">
          Работаем ежедневно с 9:00 до 21:00. Отвечаем на WhatsApp и в мессенджерах.
        </p>
      </div>

      <ManagerCards />

      <a
        data-testid={TOUR.contactEmail}
        href="mailto:Pepsi-girl2007@mail.ru"
        className="mt-8 flex items-center justify-center gap-3 bg-white rounded-2xl p-5 border border-[#F0DFC1] hover:border-teal/50 hover:shadow-lg transition-all group"
      >
        <div className="w-11 h-11 rounded-full bg-coral/15 text-coral flex items-center justify-center">
          <Mail className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-teal">E-mail</div>
          <div className="font-semibold text-[#063B4E] group-hover:text-coral transition-colors">
            Pepsi-girl2007@mail.ru
          </div>
        </div>
      </a>

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-5 bg-[#063B4E] text-white rounded-2xl p-6 sm:p-8">
        <div className="max-w-xl">
          <div className="text-xs uppercase tracking-[0.2em] text-coral mb-2">Персональный подбор</div>
          <div className="font-display text-xl sm:text-2xl leading-tight">
            Не хотите искать сами? Мы подберём тур бесплатно и без обязательств.
          </div>
        </div>
        <Button
          data-testid={TOUR.fallbackCtaBtn}
          onClick={onOpenLead}
          className="bg-coral hover:bg-[#d95a3f] text-white rounded-full h-12 px-7 text-base font-semibold shadow-lg shadow-coral/25 whitespace-normal sm:whitespace-nowrap w-full sm:w-auto"
        >
          Оставить заявку на индивидуальный подбор
        </Button>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-[#063B4E] text-white/80 py-12">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-6 md:grid-cols-3 items-start">
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-full bg-teal flex items-center justify-center rotate-[-12deg]">
          <Plane className="w-4 h-4 text-white -rotate-45" />
        </div>
        <div>
          <div className="font-display text-lg text-white leading-tight">Барселона</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Туры по всему миру</div>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="text-white/60 text-[11px] uppercase tracking-widest">Связаться</div>
        <a href="tel:+79223831919" className="block hover:text-white">+7 922 383-19-19 · Наталья</a>
        <a href="tel:+79129811222" className="block hover:text-white">+7 912 981-12-22 · Инна</a>
        <a href="tel:+79824859750" className="block hover:text-white">+7 982 485-97-50 · Юлия</a>
        <a
          href="mailto:Pepsi-girl2007@mail.ru"
          className="inline-flex items-center gap-2 text-coral hover:text-white pt-1"
        >
          <Mail className="w-3.5 h-3.5" />
          Pepsi-girl2007@mail.ru
        </a>
      </div>

      <div className="text-sm text-white/60 md:text-right">
        © {new Date().getFullYear()} «Барселона». Туры по всему миру.
      </div>
    </div>
  </footer>
);

const Home = () => {
  const [leadOpen, setLeadOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);
  const [lastFilters, setLastFilters] = useState(null);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState("lead");
  const searchTimer = useRef(null);

  const handleSearch = (filters) => {
    setLoading(true);
    setTours([]);
    setLastFilters(filters);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      const results = generateTours(filters);
      setTours(results);
      setLoading(false);
      setTimeout(() => scrollToId("search-results-anchor"), 100);
    }, 1200);
  };

  const openLead = (src = "lead") => {
    setSelectedTour(null);
    setSource(src);
    setLeadOpen(true);
  };

  const handleSelectTour = (tour) => {
    setSelectedTour(tour);
    setSource("tour_card");
    setLeadOpen(true);
  };

  const handleModalOpenChange = (v) => {
    setLeadOpen(v);
    if (!v) setSelectedTour(null);
  };

  return (
    <div className="min-h-screen bg-cream">
      <Header onOpenLead={() => openLead("lead")} />
      <Hero />
      <SearchSection
        onSearch={handleSearch}
        onPickForMe={() => openLead("search_widget")}
        loading={loading}
        tours={tours}
        onSelectTour={handleSelectTour}
      />
      <span id="search-results-anchor" />
      <OwnerSection />
      <Contacts onOpenLead={() => openLead("lead")} />
      <Footer />

      <LeadRequestModal
        open={leadOpen}
        onOpenChange={handleModalOpenChange}
        tour={selectedTour}
        source={source}
        filters={lastFilters}
      />
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin/requests" element={<AdminRequests />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
